import { useId, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { US_STATES } from "../lib/states";
import type { StateLicense } from "../types/api";
import { Button } from "./Button";
import { LABEL_SKIN } from "./FieldLabel";
import { useFormSkin } from "./FormSkinContext";
import { Input } from "./Input";
import { Select } from "./Select";

interface StateLicensesEditorProps {
  value: StateLicense[];
  onChange: (next: StateLicense[]) => void;
  disabled?: boolean | undefined;
  /** Index-aligned per-row messages (a bad license number); absent/null = no error. */
  rowErrors?: readonly (string | null)[] | undefined;
  /** List-level message (no rows / duplicate states) rendered under the list. */
  error?: string | undefined;
}

/**
 * Broker licensed-states editor: one row per state license, plus an "Add state" button.
 *
 * Each row's select excludes states that other rows use, so duplicates can't
 * be entered; the list-level `error` covers duplicates already in the data. It
 * is controlled: the caller owns the rows and is the single write path, so the
 * partner form's draft autosave picks up every edit. The partner profile form
 * and the admin org-edit modal both mount it.
 */
export function StateLicensesEditor({
  value,
  onChange,
  disabled,
  rowErrors,
  error,
}: StateLicensesEditorProps): ReactNode {
  const labelSkin = LABEL_SKIN[useFormSkin()];
  const errorId = useId();
  const used = new Set(value.map((r) => r.state).filter(Boolean));
  const setRow = (i: number, patch: Partial<StateLicense>): void =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <fieldset
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
    >
      <legend className={cn("mb-1.5", labelSkin.label)}>
        Licensed states
        <span aria-hidden="true" className={labelSkin.asterisk}>
          *
        </span>
      </legend>
      <div className="space-y-3">
        {value.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] items-start gap-2"
          >
            <Select
              label="License state"
              value={row.state}
              disabled={disabled}
              onChange={(e) => setRow(i, { state: e.target.value })}
            >
              <option value="">Select…</option>
              {US_STATES.filter((s) => s.code === row.state || !used.has(s.code)).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code}
                </option>
              ))}
            </Select>
            <Input
              label="License number"
              value={row.licenseNumber}
              disabled={disabled}
              error={rowErrors?.[i] ?? undefined}
              onChange={(e) => setRow(i, { licenseNumber: e.target.value })}
            />
            {/* ponytail: the top margin is fixed at one field label's height
                (its line height plus `mb-1.5`), so the button sits on the input
                row even when a row error renders below. A label type-size
                change breaks the alignment. */}
            <Button
              type="button"
              variant="soft"
              size="sm"
              className="mt-[26px]"
              disabled={disabled}
              aria-label={`Remove ${row.state || "state"} license`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-danger mt-1 text-xs">
          {error}
        </p>
      )}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-3"
        disabled={disabled || used.size >= US_STATES.length}
        onClick={() => onChange([...value, { state: "", licenseNumber: "" }])}
      >
        Add state
      </Button>
    </fieldset>
  );
}
